/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';

import { ErrorMessageWithIcon } from '../../../../components/ErrorMessageWithIcon/ErrorMessageWithIcon';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { PageOverlay } from '../../../../components/PageOverlay/PageOverlay';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../i18n';
import { SuperuserDatasetApiService } from '../../../../services/SuperuserDatasetApiService';
import { useFormStyles } from '../../../../styles/form';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { SUDataset } from '../../../../types/superuser/dataset/SUDataset';
import { SUDatasetUpdateRequest } from '../../../../types/superuser/dataset/SUDatasetUpdateRequest';
import { SUDatasetCategory } from '../../../../types/superuser/dataset-category/SUDatasetCategory';
import { SUDatasetCategoryI18n } from '../../../../types/superuser/dataset-category/SUDatasetCategoryI18n';
import { fetchAdminDatasetsCategories } from '../../../../utils/queryFns';
import { reactToUpdateAdminDatasets } from '../../../../utils/reactions';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useIsMounted } from '../../../../utils/useIsMounted';
import { ignoreApiErrors, validationSchema } from './EditDatasetForm.consts';
import { useEditDatasetFormStyles } from './EditDatasetForm.styles';
import { EditDatasetFormValues } from './EditDatasetForm.types';

export type EditDatasetFormType = {
  isOpen: boolean;
  closeOverlay: () => void;
  dataset: SUDataset | null;
};

export function EditDatasetForm(props: EditDatasetFormType) {
  const { isOpen, closeOverlay, dataset } = props;
  const classes = useEditDatasetFormStyles();
  const formClasses = useFormStyles();
  const { t, i18n } = useTranslation(Namespaces.Admin);

  const isEditing = useMemo(() => !!dataset, [dataset]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const isMounted = useIsMounted();

  const queryDatasetsCategories = useQuery(fetchAdminDatasetsCategories.key, fetchAdminDatasetsCategories.fn, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  const datasetsCategories = useMemo(() => queryDatasetsCategories.data?.payload ?? [], [queryDatasetsCategories]);

  const isDatasetsCategoriesLoading = useMemo(
    () => queryDatasetsCategories.status === 'loading',
    [queryDatasetsCategories.status],
  );

  const isDatasetsCategoriesError = useMemo(
    () => queryDatasetsCategories.error && queryDatasetsCategories.status === 'error',
    [queryDatasetsCategories.error, queryDatasetsCategories.status],
  );

  const initialValues = useMemo<EditDatasetFormValues>(() => {
    return {
      identifier: dataset?.identifier || '',
      name: dataset?.name || '',
      description: dataset?.description || '',
      database: dataset?.database || '',
      schema: dataset?.schema || '',
      rawSizeMb: dataset?.rawSizeMb || '',
      finalSizeMb: dataset?.finalSizeMb || '',
      importSizeMb: dataset?.importSizeMb || '',
      visible: dataset?.visible || false,
      categoryId: dataset?.category.id || '',
    };
  }, [dataset]);

  const onSubmit = async (values: EditDatasetFormValues) => {
    try {
      setIsLoading(true);
      resetApiError();
      const requestBody: SUDatasetUpdateRequest = {
        ...values,
        rawSizeMb: values.rawSizeMb.toString(),
        finalSizeMb: values.finalSizeMb.toString(),
        importSizeMb: values.importSizeMb.toString(),
      };
      if (isEditing && dataset) {
        await SuperuserDatasetApiService.updateDataset({
          id: dataset.id,
          data: requestBody,
        });
      } else {
        await SuperuserDatasetApiService.createDataset({
          data: requestBody,
        });
      }
      if (!isMounted.current) {
        return;
      }
      await reactToUpdateAdminDatasets();
      setIsLoading(false);
      closeOverlay();
    } catch (e) {
      if (!isMounted.current) {
        return;
      }
      const error = e as ApiClientError;
      const message = error.response?.data.message;
      setIsLoading(false);
      if (ignoreApiErrors.indexOf(message as string) === -1) {
        setIsOpenErrorModal(true);
      }
      if (error.response) {
        setApiError(error);
      }
    }
  };

  const formik = useFormik<EditDatasetFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { handleSubmit, values, handleChange, dirty, errors, touched, handleBlur, resetForm, setFieldValue } = formik;

  const { resetApiError, setApiError, getFieldApiError, hasFieldApiError, resetFieldApiError } = useApiValidation();

  const selectedCategory = useMemo(
    () => datasetsCategories.find(c => c.id === values.categoryId) ?? null,
    [datasetsCategories, values.categoryId],
  );

  const handleCloseOverlayForm = () => {
    resetForm();
    resetApiError();
    closeOverlay();
  };

  const hasError = (field: keyof EditDatasetFormValues): boolean => {
    return !!touched[field] && (!!errors[field] || hasFieldApiError(field));
  };

  const getError = (field: keyof EditDatasetFormValues): string => {
    return errors[field] || getFieldApiError(field);
  };

  const i18nNameSelector = useCallback(
    (language: string, i18n: SUDatasetCategoryI18n[]) => i18n.find(i => i.language === language)?.title || '—',
    [],
  );

  return (
    <PageOverlay
      open={isOpen}
      classes={{ childrenBlock: 'd-flex' }}
      showCloseBtn
      onCloseBtnClick={handleCloseOverlayForm}
      disableRestoreFocus
      name="edit-dataset"
    >
      {isDatasetsCategoriesError ? (
        <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
      ) : (
        <div className={classes.formRoot}>
          <Typography variant={'h2'} className={classes.title}>
            {isEditing ? t('datasets.form.edit_title') : t('datasets.form.add_title')}
          </Typography>
          <HUSpin spinning={isLoading || isDatasetsCategoriesLoading} delay={50}>
            <form onSubmit={handleSubmit}>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                variant="outlined"
                error={hasError('identifier')}
              >
                <InputLabel htmlFor="identifier">{t('datasets.form.identifier')}</InputLabel>
                <OutlinedInput
                  id="identifier"
                  name="identifier"
                  label={t('datasets.form.identifier')}
                  fullWidth
                  type="text"
                  value={values.identifier}
                  onChange={e => {
                    resetFieldApiError('identifier');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={hasError('identifier')}
                />
                {hasError('identifier') && (
                  <ErrorMessageWithIcon message={t(`datasets.form.errors.identifier.${getError('identifier')!}`)} />
                )}
              </FormControl>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                variant="outlined"
                error={hasError('name')}
              >
                <InputLabel htmlFor="name">{t('datasets.form.name')}</InputLabel>
                <OutlinedInput
                  id="name"
                  name="name"
                  label={t('datasets.form.name')}
                  fullWidth
                  type="text"
                  value={values.name}
                  onChange={e => {
                    resetFieldApiError('name');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={hasError('name')}
                />
                {hasError('name') && (
                  <ErrorMessageWithIcon message={t(`datasets.form.errors.name.${getError('name')!}`)} />
                )}
              </FormControl>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                variant="outlined"
                error={hasError('description')}
              >
                <InputLabel htmlFor="description">{t('datasets.form.description')}</InputLabel>
                <OutlinedInput
                  id="description"
                  name="description"
                  multiline={true}
                  label={t('datasets.form.description')}
                  fullWidth
                  value={values.description}
                  onChange={e => {
                    resetFieldApiError('description');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={hasError('description')}
                />
                {hasError('description') && (
                  <ErrorMessageWithIcon message={t(`datasets.form.errors.description.${getError('description')!}`)} />
                )}
              </FormControl>
              <FormControl
                className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
                variant="outlined"
                error={hasError('categoryId')}
              >
                <InputLabel htmlFor="categoryId">{t('datasets.form.categoryId')}</InputLabel>
                <HUAutocomplete
                  popupIcon={<ArrowRoundedIcon />}
                  renderInput={renderParams => (
                    <OutlinedInput
                      {...destructRenderInputParams(renderParams)}
                      name="categoryId"
                      label={t('datasets.form.categoryId')}
                      onBlur={handleBlur}
                    />
                  )}
                  id="categoryId"
                  options={datasetsCategories}
                  disableClearable={true}
                  value={selectedCategory ?? undefined}
                  onBlur={handleBlur}
                  onChange={(_e: any, value: SUDatasetCategory | null) => {
                    if (value !== null) {
                      void setFieldValue('categoryId', value && value.id, true);
                      resetFieldApiError('categoryId');
                    }
                  }}
                  getOptionLabel={v => i18nNameSelector(i18n.language, v.i18n)}
                />
                {hasError('categoryId') && (
                  <ErrorMessageWithIcon message={t(`datasets.form.errors.categoryId.${getError('categoryId')!}`)} />
                )}
              </FormControl>
              {!isEditing && (
                <>
                  <FormControl
                    className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                    variant="outlined"
                    error={hasError('database')}
                  >
                    <InputLabel htmlFor="database">{t('datasets.form.database')}</InputLabel>
                    <OutlinedInput
                      id="database"
                      name="database"
                      label={t('datasets.form.database')}
                      fullWidth
                      type="text"
                      value={values.database}
                      onChange={e => {
                        resetFieldApiError('database');
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={hasError('database')}
                    />
                    {hasError('database') && (
                      <ErrorMessageWithIcon message={t(`datasets.form.errors.database.${getError('database')!}`)} />
                    )}
                  </FormControl>
                  <FormControl
                    className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                    variant="outlined"
                    error={hasError('schema')}
                  >
                    <InputLabel htmlFor="schema">{t('datasets.form.schema')}</InputLabel>
                    <OutlinedInput
                      id="schema"
                      name="schema"
                      label={t('datasets.form.schema')}
                      fullWidth
                      type="text"
                      value={values.schema}
                      onChange={e => {
                        resetFieldApiError('schema');
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={hasError('schema')}
                    />
                    {hasError('schema') && (
                      <ErrorMessageWithIcon message={t(`datasets.form.errors.schema.${getError('schema')!}`)} />
                    )}
                  </FormControl>
                </>
              )}
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                variant="outlined"
                error={hasError('rawSizeMb')}
              >
                <InputLabel htmlFor="rawSizeMb">{t('datasets.form.rawSizeMb')}</InputLabel>
                <OutlinedInput
                  id="rawSizeMb"
                  name="rawSizeMb"
                  label={t('datasets.form.rawSizeMb')}
                  fullWidth
                  type="number"
                  value={values.rawSizeMb}
                  onChange={e => {
                    resetFieldApiError('rawSizeMb');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={hasError('rawSizeMb')}
                />
                {hasError('rawSizeMb') && (
                  <ErrorMessageWithIcon message={t(`datasets.form.errors.rawSizeMb.${getError('rawSizeMb')!}`)} />
                )}
              </FormControl>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                variant="outlined"
                error={hasError('finalSizeMb')}
              >
                <InputLabel htmlFor="finalSizeMb">{t('datasets.form.finalSizeMb')}</InputLabel>
                <OutlinedInput
                  id="finalSizeMb"
                  name="finalSizeMb"
                  label={t('datasets.form.finalSizeMb')}
                  fullWidth
                  type="number"
                  value={values.finalSizeMb}
                  onChange={e => {
                    resetFieldApiError('finalSizeMb');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={hasError('finalSizeMb')}
                />
                {hasError('finalSizeMb') && (
                  <ErrorMessageWithIcon message={t(`datasets.form.errors.finalSizeMb.${getError('finalSizeMb')!}`)} />
                )}
              </FormControl>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                variant="outlined"
                error={hasError('importSizeMb')}
              >
                <InputLabel htmlFor="importSizeMb">{t('datasets.form.importSizeMb')}</InputLabel>
                <OutlinedInput
                  id="importSizeMb"
                  name="importSizeMb"
                  label={t('datasets.form.importSizeMb')}
                  fullWidth
                  type="number"
                  value={values.importSizeMb}
                  onChange={e => {
                    resetFieldApiError('importSizeMb');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={hasError('importSizeMb')}
                />
                {hasError('importSizeMb') && (
                  <ErrorMessageWithIcon message={t(`datasets.form.errors.importSizeMb.${getError('importSizeMb')!}`)} />
                )}
              </FormControl>
              <div className={classes.checkboxes}>
                <FormControlLabel
                  name="visible"
                  checked={values.visible}
                  onChange={event => handleChange(event)}
                  control={<Checkbox color="primary" />}
                  label={t('datasets.form.visible')}
                  labelPlacement="end"
                />
              </div>
              {/* {isEditing && (
                  <Alert severity="warning" className={classes.mt10}>
                    {t("datasets.form.warnings.existing_warn")}
                  </Alert>
                )} */}
              <Button variant="contained" className={classes.buttonApply} type="submit" disabled={!dirty}>
                {isEditing ? t('datasets.form.save') : t('datasets.form.add')}
              </Button>
            </form>
          </HUSpin>
          <SimpleErrorModal
            open={isOpenErrorModal}
            message={t(`${Namespaces.Common}:errors.unexpected_error`)}
            onOk={() => {
              setIsOpenErrorModal(false);
              resetApiError();
            }}
          />
        </div>
      )}
    </PageOverlay>
  );
}
